import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { ArrowLeft, Calendar as CalendarIcon, Phone, Mail } from "lucide-react";
import { parseISO, isSameDay } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import axios from "axios";
import { getBackendUrl } from "@/utils/apiBase";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Rose-gold palette — kept local for consistency with BookingPage
const COLORS = {
  roseGold: "#b8977e",
  roseGoldDark: "#a68770",
  cream: "#faf8f5",
  border: "#e8d5c4",
  dark: "#2d2319",
  text: "#5a5a5a",
  warn: "#f59e0b",
  danger: "#dc2626",
};

const CalendarPage = () => {
  const navigate = useNavigate();
  const [bookedMap, setBookedMap] = useState({}); // { "YYYY-MM-DD": count }
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date());

  useEffect(() => {
    fetchBookedDates();
  }, []);

  const fetchBookedDates = async () => {
    try {
      // Public endpoint — returns { booked_dates: [{ date, count }, ...] }
      const r = await axios.get(`${API}/availability/booked-dates`);
      const list = r.data?.booked_dates || r.data || [];
      const map = {};
      list.forEach((b) => {
        if (typeof b === "string") map[b] = (map[b] || 0) + 1;
        else if (b?.date) map[b.date] = (map[b.date] || 0) + (b.count || 1);
      });
      setBookedMap(map);
    } catch (e) {
      console.error("Erreur chargement disponibilités:", e);
      toast.error("Erreur lors du chargement du calendrier");
    } finally {
      setLoading(false);
    }
  };

  const formatYMD = (d) => {
    if (!d) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const dateStatus = (date) => {
    if (!date) return "available";
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return "past";
    const count = bookedMap[formatYMD(date)] || 0;
    if (count >= 2) return "full";
    if (count === 1) return "one-left";
    return "available";
  };

  const handleDateSelect = (date) => {
    if (!date) return;
    const status = dateStatus(date);
    if (status === "past") return;
    if (status === "full") {
      toast.error("Cette date est complète (2/2 photobooths réservés)");
      return;
    }
    setSelectedDate(date);
    navigate("/reserver", { state: { selectedDate: date } });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: COLORS.cream }}>
        <div className="text-center">
          <div
            className="mx-auto rounded-full"
            style={{
              width: 44, height: 44,
              border: `3px solid ${COLORS.border}`,
              borderTopColor: COLORS.roseGold,
              animation: "spin 0.8s linear infinite",
            }}
          />
          <p className="mt-4" style={{ color: COLORS.text, fontFamily: "'Montserrat','DM Sans',sans-serif" }}>
            Chargement du calendrier...
          </p>
        </div>
      </div>
    );
  }

  const cardStyle = {
    background: "#ffffff",
    border: `1px solid ${COLORS.border}`,
    borderRadius: "16px",
    boxShadow: "0 4px 24px rgba(184,151,126,0.08)",
    overflow: "hidden",
    marginBottom: "24px",
  };
  const cardHeaderStyle = {
    background: COLORS.cream,
    padding: "20px 24px",
    borderBottom: `1px solid ${COLORS.border}`,
  };
  const legendPill = (bg, border) => ({
    width: 14, height: 14, borderRadius: 4, background: bg,
    border: `1px solid ${border || bg}`, display: "inline-block", flexShrink: 0,
  });

  return (
    <div className="min-h-screen" style={{ background: COLORS.cream }} data-testid="calendar-page">
      {/* Header */}
      <div style={{ background: "#fff", borderBottom: `1px solid ${COLORS.border}`, position: "sticky", top: 0, zIndex: 10 }}>
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            data-testid="calendar-back-btn"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "transparent", border: `1px solid ${COLORS.border}`,
              color: COLORS.roseGold, padding: "8px 14px", borderRadius: 24,
              fontFamily: "'Montserrat','DM Sans',sans-serif", fontSize: 13, cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.roseGold; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = COLORS.roseGold; }}
          >
            <ArrowLeft size={14} /> Retour
          </button>
          <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: COLORS.dark, margin: 0, letterSpacing: "0.01em" }}>
            Calendrier des disponibilités
          </h1>
          <div style={{ width: 80 }} />
        </div>
      </div>

      {/* Main */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Urgency banner — computed from current month visible on the calendar */}
        {(() => {
          const y = month.getFullYear();
          const m = month.getMonth();
          const last = new Date(y, m + 1, 0).getDate();
          const today = new Date(); today.setHours(0, 0, 0, 0);
          let totalAvailable = 0;
          let availableSaturdays = 0;
          for (let d = 1; d <= last; d++) {
            const date = new Date(y, m, d);
            if (date < today) continue;
            const count = bookedMap[formatYMD(date)] || 0;
            if (count < 2) {
              totalAvailable++;
              if (date.getDay() === 6) availableSaturdays++;
            }
          }
          const monthLabel = month.toLocaleDateString("fr-FR", { month: "long" });
          let icon, text, accent;
          if (totalAvailable === 0) {
            icon = "🔴"; text = `Aucune date disponible en ${monthLabel}`; accent = COLORS.danger;
          } else if (totalAvailable <= 3) {
            icon = "🔴"; text = `Seulement ${totalAvailable} date${totalAvailable > 1 ? "s" : ""} disponible${totalAvailable > 1 ? "s" : ""} en ${monthLabel} !`; accent = COLORS.danger;
          } else if (totalAvailable <= 6) {
            icon = "🟠"; text = `Plus que ${totalAvailable} dates libres en ${monthLabel}`; accent = COLORS.warn;
          } else if (availableSaturdays <= 2 && availableSaturdays > 0) {
            icon = "⚡"; text = `${availableSaturdays} samedi${availableSaturdays > 1 ? "s" : ""} disponible${availableSaturdays > 1 ? "s" : ""} en ${monthLabel}`; accent = COLORS.warn;
          } else {
            icon = "✅"; text = `${totalAvailable} dates disponibles en ${monthLabel}`; accent = COLORS.roseGold;
          }
          return (
            <div
              data-testid="urgency-banner"
              style={{
                background: COLORS.cream,
                border: `1px solid ${COLORS.roseGold}`,
                borderLeft: `4px solid ${accent}`,
                borderRadius: 10,
                padding: "14px 20px",
                marginBottom: 24,
                display: "flex",
                alignItems: "center",
                gap: 12,
                fontFamily: "'Montserrat','DM Sans',sans-serif",
                color: COLORS.dark,
              }}
            >
              <span style={{ fontSize: 20, lineHeight: 1 }}>{icon}</span>
              <span style={{ fontSize: 14, fontWeight: 600 }}>{text}</span>
            </div>
          );
        })()}
        {/* Intro card */}
        <div style={cardStyle} data-testid="calendar-intro-card">
          <div style={cardHeaderStyle}>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, color: COLORS.dark, margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
              <CalendarIcon size={22} style={{ color: COLORS.roseGold }} />
              Réservez votre date
            </h2>
            <p style={{ color: COLORS.text, fontFamily: "'Montserrat','DM Sans',sans-serif", fontSize: 13, margin: "6px 0 0 0" }}>
              Sélectionnez une date disponible pour démarrer votre demande.
            </p>
          </div>
          <div style={{ padding: "18px 24px" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 20, fontFamily: "'Montserrat','DM Sans',sans-serif", fontSize: 13, color: COLORS.text }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={legendPill("#ffffff", COLORS.border)} /> Date disponible
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={legendPill("#fff3e0", COLORS.warn)} /> 1 photobooth restant
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={legendPill("#fde4e4", COLORS.danger)} /> Complet
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={legendPill(COLORS.cream, COLORS.roseGold)} /> Date sélectionnée
              </div>
            </div>
          </div>
        </div>

        {/* Calendar card */}
        <div style={cardStyle} data-testid="calendar-main-card">
          <div style={{ padding: "24px", display: "flex", justifyContent: "center" }}>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              month={month}
              onMonthChange={setMonth}
              locale={fr}
              disabled={(date) => dateStatus(date) === "past" || dateStatus(date) === "full"}
              modifiers={{
                full: (date) => dateStatus(date) === "full",
                oneLeft: (date) => dateStatus(date) === "one-left",
              }}
              modifiersStyles={{
                full: { background: "#fde4e4", color: COLORS.danger, textDecoration: "line-through" },
                oneLeft: { background: "#fff3e0", color: COLORS.warn, fontWeight: 600 },
              }}
              className="rounded-md"
            />
          </div>
          <div style={{ padding: "0 24px 24px 24px" }}>
            <div style={{ background: COLORS.cream, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "14px 18px", textAlign: "center" }}>
              <p style={{ margin: 0, color: COLORS.dark, fontFamily: "'Montserrat','DM Sans',sans-serif", fontSize: 13 }}>
                💡 <strong style={{ color: COLORS.roseGold }}>Cliquez sur une date</strong> disponible pour démarrer votre demande de réservation
              </p>
              <p style={{ margin: "6px 0 0 0", color: COLORS.text, fontSize: 11 }}>
                Nous disposons de 2 photobooths — chaque date peut accueillir 2 événements
              </p>
            </div>
          </div>
        </div>

        {/* Contact card */}
        <div style={cardStyle} data-testid="calendar-contact-card">
          <div style={cardHeaderStyle}>
            <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: COLORS.dark, margin: 0 }}>
              Besoin d'aide ?
            </h3>
          </div>
          <div style={{ padding: "22px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <Phone size={18} style={{ color: COLORS.roseGold }} />
              <div style={{ fontFamily: "'Montserrat','DM Sans',sans-serif", fontSize: 13, color: COLORS.text }}>
                <div style={{ fontWeight: 600, color: COLORS.dark, marginBottom: 2 }}>Téléphone</div>
                <a href="tel:+33679393429" style={{ color: COLORS.roseGold, textDecoration: "none" }}
                   onMouseEnter={(e) => (e.currentTarget.style.color = COLORS.roseGoldDark)}
                   onMouseLeave={(e) => (e.currentTarget.style.color = COLORS.roseGold)}>
                  +33 6 79 39 34 29
                </a>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Mail size={18} style={{ color: COLORS.roseGold }} />
              <div style={{ fontFamily: "'Montserrat','DM Sans',sans-serif", fontSize: 13, color: COLORS.text }}>
                <div style={{ fontWeight: 600, color: COLORS.dark, marginBottom: 2 }}>Email</div>
                <a href="mailto:walid.societe.pro@gmail.com" style={{ color: COLORS.roseGold, textDecoration: "none" }}
                   onMouseEnter={(e) => (e.currentTarget.style.color = COLORS.roseGoldDark)}
                   onMouseLeave={(e) => (e.currentTarget.style.color = COLORS.roseGold)}>
                  walid.societe.pro@gmail.com
                </a>
              </div>
            </div>
            <p style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${COLORS.border}`, color: COLORS.text, fontFamily: "'Montserrat','DM Sans',sans-serif", fontSize: 12, lineHeight: 1.6 }}>
              Notre équipe est à votre disposition pour répondre à toutes vos questions et vous aider à organiser votre événement.
            </p>
          </div>
        </div>
      </div>

      {/* shadcn calendar harmonization */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .rdp { --rdp-cell-size: 44px; }
        .rdp-caption_label { font-family: 'Playfair Display', serif !important; color: ${COLORS.dark} !important; font-size: 16px !important; font-weight: 600 !important; }
        .rdp-nav_button { color: ${COLORS.roseGold} !important; }
        .rdp-nav_button:hover { background: ${COLORS.cream} !important; }
        .rdp-head_cell { color: ${COLORS.roseGold} !important; font-family: 'Montserrat',sans-serif !important; font-size: 11px !important; font-weight: 600 !important; letter-spacing: 0.04em; text-transform: uppercase; }
        .rdp-day { border-radius: 8px !important; font-family: 'DM Sans', sans-serif !important; }
        .rdp-day:hover:not([disabled]) { background: ${COLORS.cream} !important; color: ${COLORS.dark} !important; }
        .rdp-day_selected, .rdp-day_selected:hover { background: ${COLORS.cream} !important; color: ${COLORS.dark} !important; border: 1.5px solid ${COLORS.roseGold} !important; font-weight: 600 !important; }
        .rdp-day_today:not(.rdp-day_selected) { color: ${COLORS.roseGold} !important; font-weight: 700 !important; position: relative; }
        .rdp-day_today::after { content: ''; position: absolute; bottom: 3px; left: 50%; transform: translateX(-50%); width: 4px; height: 4px; border-radius: 50%; background: ${COLORS.roseGold}; }
        .rdp-day_disabled { color: #cfcfcf !important; opacity: 0.55 !important; cursor: not-allowed !important; }
      `}</style>
    </div>
  );
};

export default CalendarPage;
